import ExpoConstants from 'expo-constants';
import { useAlertDialogStore } from 'src/domain/stores/useAlertDialogStore';
import type { InstallDrawerEscapeBridge } from 'src/presentation/helpers/types/DrawerBridgeTypes';

/**
 * The one thing the app says to the page hosting it in a panel.
 *
 * It only speaks when actually embedded (`window.parent !== window`), so a direct visit to the web
 * app posts nothing at all, and it posts to the backend origin of THIS environment, never to '*'.
 * A wildcard would hand a message to whatever page managed to embed the app.
 *
 * The app deliberately does NOT report which screen it is on: the host's address bar stays on
 * /app and does not follow the panel, same as the leaseapp. A deep link still works the other way
 * around, because /app/<path> is a real route that puts the iframe on that screen.
 */
const HOST_ORIGIN = (ExpoConstants.expoConfig?.extra?.baseUrl as string | undefined) ?? '';

/**
 * Escape has to close the drawer, and the host cannot see a key pressed inside a cross-origin
 * iframe -- which is every keypress once someone is actually using the app. So the app forwards
 * the intent and the host decides what to do with it.
 *
 * This is the second and last thing the app may say. It carries no path, no target and no token:
 * the host closes its own panel and nothing else.
 */
export const installDrawerEscapeBridge: InstallDrawerEscapeBridge = () => {
	if (typeof window === 'undefined' || window.parent === window || HOST_ORIGIN === '') {
		return () => undefined;
	}

	const onKeyDown = (event: KeyboardEvent): void => {
		if (event.key !== 'Escape') {
			return;
		}

		// A dialog on screen gets Escape first, and only the dialog. It closes on the keyup that
		// follows; asking the host to close the whole panel here would take the question away
		// instead of answering it.
		if (useAlertDialogStore.getState().queue.length > 0) {
			return;
		}

		window.parent.postMessage({ source: 'topicus-app', type: 'escape' }, HOST_ORIGIN);
	};

	window.addEventListener('keydown', onKeyDown);

	return () => {
		window.removeEventListener('keydown', onKeyDown);
	};
};
export default installDrawerEscapeBridge;
